import React, { useMemo, useState } from 'react';
import { cn } from '../../../../utils/cn';
import { ReservationUiModel } from '../../../../shared/ui-models/reservation.ui-model';
import { ReservationsDashboardUpdates } from '../../../shared/services/reservations-dashboard.data';
import { APP_ROUTES, PUBLIC_PATH } from '../../../../shared/config/routes-config';
import { RouteConfig } from '../../../../shared/interfaces/routes-config.interface';
import {
  getReservationSourceImage,
  getReservationSourceLabel,
  getReservationChannelImage,
  getReservationChannelLabel
} from '../../../../shared/services/helpers/reservation-source-channel.helper';

type QueueKey = keyof ReservationsDashboardUpdates;

const TABS: Array<{ key: QueueKey; label: string; empty: string }> = [
  {
    key: 'upcomingCheckIns',
    label: 'Upcoming in the next 3 days',
    empty: 'No arrivals in the next three days.'
  },
  {
    key: 'upcomingDepartures',
    label: 'Departures in the next 3 days',
    empty: 'No departures in the next three days.'
  },
  {
    key: 'createdRecently',
    label: 'New reservations in the last 3 days',
    empty: 'No reservations created in the last three days.'
  },
  { key: 'currentlyHosting', label: 'Currently Hosting', empty: 'No guests are in house right now.' }
];

const COLUMNS = [
  'Reservation #',
  'Channel',
  'Guest Name',
  'Room Name',
  'Area',
  'Check In',
  'Check Out'
];

const cellClassName = 'px-5 text-[14px] font-gilroy-medium text-gray-900';

const formatDate = (value: number) => new Date(value).toDateString();

const openReservation = (id: string) =>
  window.open(
    PUBLIC_PATH +
      (APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig).EDIT.FULL_PATH.replace(':id', id),
    '_blank'
  );

const Row = React.memo(({ reservation }: { reservation: ReservationUiModel }) => (
  <tr
    className="h-[56px] cursor-pointer border-b border-gray-200 transition-colors last:border-b-0 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-700"
    onClick={() => openReservation(reservation.id)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openReservation(reservation.id);
      }
    }}
    role="button"
    tabIndex={0}
  >
    <td className={cellClassName}>#{reservation.id}</td>
    <td className="px-5">
      <span className="flex items-center gap-2">
        <img
          className="h-7 w-7 object-contain"
          src={getReservationSourceImage(reservation.source)}
          alt={getReservationSourceLabel(reservation.source)}
        />
        <span aria-hidden="true" className="text-neutral-400">
          &rsaquo;
        </span>
        <img
          className="h-7 w-7 object-contain"
          src={getReservationChannelImage(reservation.channel)}
          alt={getReservationChannelLabel(reservation.channel)}
        />
      </span>
    </td>
    <td className={cellClassName}>{reservation.primaryGuestName}</td>
    <td className={cellClassName}>{reservation.unitName.replace(/(^.*\(|\).*$)/g, '')}</td>
    <td className={cellClassName}>{reservation.area}</td>
    <td className={cn(cellClassName, 'whitespace-nowrap')}>{formatDate(reservation.checkInDate)}</td>
    <td className={cn(cellClassName, 'whitespace-nowrap')}>
      {formatDate(reservation.checkOutDate)}
    </td>
  </tr>
));

export const ReservationsPanel = ({
  updates,
  isLoading
}: {
  updates: ReservationsDashboardUpdates | null;
  isLoading: boolean;
}) => {
  const [active, setActive] = useState<QueueKey>('upcomingCheckIns');

  const rows = useMemo(() => (updates ? updates[active] : []), [updates, active]);
  const activeTab = TABS.find((tab) => tab.key === active);

  return (
    <section className="overflow-hidden rounded-[12px] border border-gray-200 bg-white shadow-[0_1px_2px_rgba(24,33,47,0.04)]">
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5">
        <h2 className="text-[22px] font-gilroy-bold text-gray-900">Reservations</h2>
        <div className="flex flex-wrap gap-3">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              aria-pressed={active === tab.key}
              onClick={() => setActive(tab.key)}
              className={cn(
                'min-h-[44px] cursor-pointer rounded-lg px-5 text-[13px] font-gilroy-medium transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700',
                active === tab.key
                  ? 'bg-primary-700 text-white'
                  : 'border border-gray-200 bg-white text-gray-900 hover:bg-gray-100'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-8">
          {[0, 1, 2, 3, 4].map((row) => (
            <span
              key={row}
              className="h-8 w-full animate-pulse rounded bg-gray-200 motion-reduce:animate-none"
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="border-t border-gray-200 px-6 py-12 text-center text-[13px] text-neutral-400">
          {activeTab?.empty}
        </p>
      ) : (
        <div className="max-h-[42rem] overflow-x-auto overflow-y-auto">
          <table className="w-full min-w-[900px] border-collapse">
            <thead className="sticky top-0 z-10 border-y border-gray-200 bg-gray-100">
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column}
                    className="h-[44px] px-5 text-left text-[13px] font-gilroy-medium text-neutral-400"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((reservation) => (
                <Row key={reservation.id} reservation={reservation} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
